import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import queryString from 'query-string'

import { apiService } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { fetchUnreadCount } from '@/reducers/notifications/notifications.actions'
import {
  CLEAR_NOTIFICATIONS,
  LOAD_NOTIFICATION,
} from '@/reducers/notifications/notifications.redux'
import { RTA_ALERT_TYPES } from '@/components/notifications/notificationActions'
import { NotificationToast } from '@/components/notifications/NotificationToast'
import RTCNotifySound from '@/assets/audio/notification1.mp3'

// QCER-989: RTA alerts are meant to be acted on in real time, so they float as
// a stacking top-right toast that auto-dismisses after 20s (they still land in
// the bell). Re-pushes of the same id are already dropped by the recentlyPlayedRef
// guard below (same as the ding), so a duplicate never re-toasts; passing the
// notification id as the sonner toast id is belt-and-suspenders — even if one
// reached this line twice it would update the existing toast, not stack a copy.
const RTA_TOAST_DURATION_MS = 20000

// Notifications flow through a dedicated WebSocket on notification-service
// (no RTC dependency). This is the canonical path — rtcSocketMiddleware no
// longer dispatches LOAD_NOTIFICATION to avoid double-delivery on accounts
// that have BOTH sockets open.

// WS push from notification-service uses snake_case keys; the HTTP
// list response (/notifications/) returns camelCase. Normalize WS
// payloads to the camelCase shape so the redux store and downstream
// consumers don't have to handle both.
const WS_TO_CAMEL = {
  notification_type: 'notificationType',
  delivery_type: 'deliveryType',
  action_url: 'actionUrl',
  ref_id: 'refId',
  ref_table: 'refTable',
  created_at: 'createdAt',
  read_at: 'readAt',
  agent_id: 'agentId',
  call_id: 'callId',
}

const normalizeNotificationKeys = (notification) => {
  const normalized = { ...notification }
  Object.entries(WS_TO_CAMEL).forEach(([snake, camel]) => {
    if (snake in normalized && !(camel in normalized)) {
      normalized[camel] = normalized[snake]
      delete normalized[snake]
    }
  })
  return normalized
}

// Auth handshake matches PR #6745's SocketClient pattern: username on the
// URL (not secret), token sent as the first WS frame so it doesn't land in
// proxy/access logs. Server validates and replies with auth_ack; only then
// do we consider the connection "stable" for reconnect-backoff purposes.
const WS_AUTH_FAILURE_CODE = 1008

const buildWsUrl = (username) => {
  const httpUrl = apiService.notification
  const wsUrl = httpUrl.replace(/^http/, 'ws')
  return `${wsUrl}/notifications/ws?${queryString.stringify({ username })}`
}

const NotificationProvider = () => {
  const dispatch = useDispatch()
  const { isOpen: rtcSocketIsOpen } = useSelector((state) => state.realtimeSocket)
  const { token, username } = useSelector((state) => state.currentUser)
  // Suppress the audible ding while the manager is actively live-listening —
  // they're already on the call, so a coaching/raised_hand notification
  // shouldn't beep in their ear. (The RTC socket used to own this suppression;
  // the bell is now the single sound source, so it lives here.) Mirror it into
  // a ref so the long-lived WS onmessage closure reads the current value.
  const isInLiveListen = useSelector((state) =>
    Boolean(state.realtimeUserAlerts?.listeningToUsername || state.commandCenter?.listeningToAgent)
  )
  const isInLiveListenRef = useRef(isInLiveListen)
  isInLiveListenRef.current = isInLiveListen
  const previousIsOpen = useRef(rtcSocketIsOpen)
  const socketRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  // De-dupes audible/banner side effects when a notification (id) arrives
  // again within the same session. The reducer dedupes the state update;
  // this dedupes the user-perceptible side effects.
  const recentlyPlayedRef = useRef(new Set())

  // Fetch unread count on initial mount
  useEffect(() => {
    if (token) {
      dispatch(fetchUnreadCount())
    }
  }, [token])

  // Re-fetch unread count when the RTC socket reconnects, in case anything
  // arrived during the gap. Kept as a safety net even though the dedicated
  // notification socket also reconciles.
  useEffect(() => {
    if (rtcSocketIsOpen && !previousIsOpen.current && token) {
      dispatch(fetchUnreadCount())
    }
    previousIsOpen.current = rtcSocketIsOpen
  }, [rtcSocketIsOpen])

  // Open a dedicated WebSocket to notification-service. Reconnects with a
  // capped exponential backoff so we don't hammer the service if it's down.
  useEffect(() => {
    if (!username || !token) {
      return undefined
    }

    let cancelled = false

    const scheduleReconnect = () => {
      if (cancelled) {
        return
      }
      const attempt = reconnectAttemptsRef.current + 1
      reconnectAttemptsRef.current = attempt
      // Exponential backoff 1s → 2s → 4s → 8s, capped at 30s. The
      // 0.5–1.0 jitter multiplier prevents a thundering-herd on
      // notification-service restarts — without it every manager tab
      // with the bell open would reconnect in the same 30s window
      // when the service rolls.
      const base = Math.min(1000 * 2 ** (attempt - 1), 30000)
      const delayMs = base * (0.5 + Math.random() * 0.5)
      // eslint-disable-next-line no-use-before-define
      reconnectTimeoutRef.current = setTimeout(() => connect(), delayMs)
    }

    const connect = () => {
      if (cancelled) {
        return
      }
      let socket
      try {
        socket = new WebSocket(buildWsUrl(username))
      } catch (error) {
        scheduleReconnect()
        return
      }
      socketRef.current = socket

      socket.onopen = () => {
        // Send the auth frame immediately — the server has accepted but
        // is waiting on this before subscribing us to the redis channel.
        // It will respond with {type: "auth_ack"} on success, which the
        // onmessage handler treats as the "connection is stable" signal.
        try {
          socket.send(JSON.stringify({ type: 'auth', token }))
        } catch (error) {
          // If the socket closed between onopen and send, onclose drives
          // the reconnect.
        }
      }

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data)
          if (payload.type === 'auth_ack') {
            // Auth accepted — reconcile missed notifications, then mark
            // the connection stable so the backoff counter resets and
            // a later transient disconnect starts from attempt=1 again.
            reconnectAttemptsRef.current = 0
            dispatch(fetchUnreadCount())
            return
          }
          if (payload.type === 'WEB_NOTIFICATION_CLEARED') {
            // Server-side revocation (raised-hand lowered). Badge reconciles
            // via refetch — local decrement math drifts on duplicate pushes.
            dispatch({
              type: CLEAR_NOTIFICATIONS,
              notificationIds: payload.notification_ids || [],
            })
            dispatch(fetchUnreadCount())
            return
          }
          if (payload.type !== 'WEB_NOTIFICATION' || !payload.notification) {
            return
          }
          // notification-service publishes snake_case keys on the WS,
          // but the bell-list HTTP response serializer emits camelCase.
          // Normalize WS payloads to camelCase here so the redux store
          // is consistent — otherwise downstream consumers (action
          // buttons, icon lookup) have to handle both shapes.
          const notification = normalizeNotificationKeys(payload.notification)
          dispatch({ type: LOAD_NOTIFICATION, notification })

          if (recentlyPlayedRef.current.has(notification.id)) {
            return
          }
          recentlyPlayedRef.current.add(notification.id)
          if (recentlyPlayedRef.current.size > 100) {
            const oldest = recentlyPlayedRef.current.values().next().value
            recentlyPlayedRef.current.delete(oldest)
          }

          if (notification.deliveryType === 'audible' && !isInLiveListenRef.current) {
            try {
              new Audio(RTCNotifySound).play()
            } catch (audioError) {
              // Browsers block autoplay until user interacts with the page.
            }
          }
          if (notification.deliveryType === 'banner') {
            toast(notification.title, { description: notification.message })
          }
          // Float RTA alerts as a top-right toast (QCER-989). Not suppressed
          // while live-listening the way the ding is — a silent visual card for
          // a NEW alert (possibly a different agent) isn't intrusive, and it
          // carries the same Listen/Chat/View-call affordances as the bell row.
          if (RTA_ALERT_TYPES.includes(notification.notificationType)) {
            toast.custom(
              (toastId) => <NotificationToast notification={notification} toastId={toastId} />,
              {
                id: notification.id,
                duration: RTA_TOAST_DURATION_MS,
                position: 'top-right',
                unstyled: true,
              }
            )
          }
        } catch (error) {
          // Non-JSON or unexpected shape — ignore
        }
      }

      socket.onclose = (event) => {
        socketRef.current = null
        // Auth rejection (server closes with 1008 when the token is invalid,
        // the user lookup fails, or the auth frame never arrived) —
        // reconnecting won't help, the token needs to be re-issued. Stop.
        if (event && event.code === WS_AUTH_FAILURE_CODE) {
          return
        }
        if (!cancelled) {
          scheduleReconnect()
        }
      }

      socket.onerror = () => {
        // onclose fires after onerror; let onclose drive the reconnect.
      }
    }

    connect()

    return () => {
      cancelled = true
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (socketRef.current) {
        try {
          socketRef.current.close()
        } catch (error) {
          // ignore
        }
        socketRef.current = null
      }
    }
  }, [username, token])

  return null
}

export default NotificationProvider
